import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, InputAdornment, TextField } from "@mui/material";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from "@mui/icons-material/Send";
import styles from './MessageForm.module.css';
import Constants from "../../utils/Constants";
import NobiEventTracker from "../../utils/NobiEventTracker";
import Events from "../../utils/Events";
import {EntryPoint} from "../Chatbot/EntryPoint";

function MessageForm({
    handleSubmit,
    disableMessages,
    isNewSearch,
    brandTheme,
    merchant,
    placeholder,
    entryPoint,
    conversationId = null,
    showSuggestions = true
}) {

    const BoxShadowStyle = "0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)";
    const DisabledColor = "#d3d3d3";

    const [userMessage, setUserMessage] = useState("");

    const inputRef = useRef(null);

    useEffect(() => {
        if (!disableMessages && inputRef.current) {
            inputRef.current.focus();
        }
    }, [disableMessages]);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }

        // Clear the input
        setUserMessage("");
    }, []);

    /**
     * Sets the user message when the user types something
     *
     * @param event {Event} The event object
     */
    function handleTextFieldChange(event) {
        setUserMessage(event.target.value);
    }

    /**
     * Handles the form submission
     *
     * @param event {Event} The event object
     */
    function handleOnSubmit(event) {
        event.preventDefault();
        if (userMessage && userMessage.trim().length >= 1) {
            let event = {
                content: userMessage,
                entryPoint: entryPoint,
                conversationId: conversationId
            }
            NobiEventTracker.trackEvent(
                merchant.id,
                entryPoint,
                Events.SendMessage,
                false,
                true,
                event);
            handleSubmit(userMessage);
            setUserMessage(""); // Clear the input after submission
        }
    }

    // Handle key down events for Enter and Shift+Enter
    function handleKeyDown(event) {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault(); // Prevent adding a new line
            handleOnSubmit(event);  // Submit the form
        }
        // If Shift+Enter, allow the default behavior (adding a new line)
    }

    function renderHelperText() {
        if (isNewSearch) {
            return <div id={styles.helperTextContainer}>
                <span style={{color: brandTheme?.general?.buttons?.backgroundColor + "59"}}>Search for anything, like "black cherry"</span>
            </div>
        }

        return <div id={styles.helperTextContainer}>
            <span className={styles.helperTextItem} style={{color: brandTheme?.general?.buttons?.backgroundColor + "59"}}>Filter by chatting:&nbsp;&nbsp; "only items under $100"</span>
            <span className={`${styles.helperTextItem} ${styles.hiddenMobileHelper}`} style={{color: brandTheme?.general?.buttons?.backgroundColor + "59"}}>"can you only show items in white"</span>
            <span className={`${styles.helperTextItem} ${styles.hiddenMobileHelper}`} style={{color: brandTheme?.general?.buttons?.backgroundColor + "59"}}>"show me items that are on sale"</span>
        </div>
    }

    function getTextFieldStyles() {
        return {
            "& .MuiOutlinedInput-root": {
                borderRadius: "500px",
                boxShadow: BoxShadowStyle,
                backgroundColor: "white",
                transition: "box-shadow 0.3s, border-color 0.3s",
                cursor: disableMessages ? "not-allowed" : "text",

                "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: disableMessages
                        ? DisabledColor
                        : brandTheme?.general?.buttons?.backgroundColor + "1A",
                    borderWidth: "1px", // Set default border width
                },

                "& .MuiOutlinedInput-root:focus-visible": {
                    outline: "none",
                    outlineOffset: "none",
                    boxShadow: "none",
                },

                "& .MuiOutlinedInput-input:focus-visible": {
                    outline: "none",
                    outlineOffset: "none",
                    boxShadow: "none",
                },

                "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: disableMessages
                        ? DisabledColor
                        : brandTheme?.general?.buttons?.backgroundColor + "1A",
                    borderWidth: "1px",
                    cursor: disableMessages ? "not-allowed" : "text"
                },

                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: disableMessages
                        ? DisabledColor
                        : brandTheme?.general?.buttons?.backgroundColor + "1A",
                    borderWidth: "1px", // Override the thicker focus border
                    cursor: disableMessages ? "not-allowed" : "text"
                }
            },

            "& .MuiOutlinedInput-input::placeholder": {
                lineHeight: entryPoint === EntryPoint.Cart ? "115%" : "175%",
                fontSize: "14px"
            }
        };
    }

    function getInputStyles() {
        let defaultStyles = {
            padding: "14px 14px 14px 18px",
            fontSize: "1em",
            lineHeight: 1.5,
            color: "#211F26"
        };

        if (entryPoint === EntryPoint.Cart) {
            return {
                ...defaultStyles,
                padding: "5px 10px 5px 13px",
                fontSize: "1em",
                lineHeight: "1em"
            }
        }

        return defaultStyles;
    }

    function getInputProps() {
        return {
            startAdornment: (
                <InputAdornment position="start">
                    <AutoAwesomeIcon
                        sx={{
                            color: brandTheme?.general?.buttons?.backgroundColor + "1A"
                        }}
                    />
                </InputAdornment>
            ),
            endAdornment: (
                <InputAdornment position="end">
                    <IconButton
                        role="button"
                        aria-label="Send Message"
                        name="send"
                        id={styles.sendButton}
                        type="submit"
                        onClick={handleOnSubmit}
                        disabled={disableMessages}
                        size={"medium"}
                        sx={{
                            backgroundColor: brandTheme?.general?.buttons?.backgroundColor,
                            color: brandTheme?.general?.buttons?.textColor,
                            '&:hover': {
                                backgroundColor: brandTheme?.general?.buttons?.hoverColor,
                            }
                        }}
                    >
                        <SendIcon />
                    </IconButton>
                </InputAdornment>
            ),
            style: getInputStyles()
        };
    }

    function getPlaceholder() {
        if (placeholder) {
            return placeholder;
        }

        return isNewSearch ? Constants.MessageFormPlaceholderForNewSearch : Constants.MessageFormPlaceholderForFiltering
    }

    function getMaxRows() {
        if (entryPoint === EntryPoint.Cart) {
            return 1;
        }

        if (window.innerWidth < Constants.MaxWidthForMobile) {
            return 2;
        }

        return 4;
    }

    return (
        <Box
            component="form"
            display="flex"
            id={styles.messageForm}
            sx={entryPoint === EntryPoint.Cart ? { padding: "0 !important", maxWidth: "100% !important" } : {}}
            onSubmit={handleOnSubmit}>
            <TextField
                value={userMessage}
                onChange={handleTextFieldChange}
                fullWidth
                multiline={entryPoint !== EntryPoint.Cart}
                maxRows={getMaxRows()}
                placeholder={getPlaceholder()}
                variant="outlined"
                size="small"
                onKeyDown={handleKeyDown}
                sx={getTextFieldStyles()}
                id={styles.messageInput}
                inputRef={inputRef}
                disabled={disableMessages}
                InputProps={getInputProps()}
                inputProps={{ 'data-testid': 'message-input' }}
            />
        </Box>
    );
}

export default MessageForm;
